import type { Ref } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type {
  DisciplineProgressReport,
  DisciplineProgressReportErrors,
  DisciplineProgressReportForm,
} from './interfaces'
import type { DisciplineProgressReportFormGroup, DisciplineProgressReportStatementKey } from './types'

import { useListsStore } from '@shared/model'

import { useDisciplineProgressReportsStore } from './progress-reports/store'
import { useDisciplineStore } from './store'
import { useDisciplineProgressReportsApi } from '../api/progress-reports.hook'
import { useDisciplineProgressReportUpdateApi } from '../api/progress-report-update.hook'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import {
  buildProgressReportPayload,
  createProgressReportForm,
  emptyProgressReportErrors,
  formatProgressReportDateRange,
  hasProgressReportErrors,
  validateProgressReportForm,
} from './discipline-progress-reports'

export interface DisciplineProgressReportSection {
  group: DisciplineProgressReportFormGroup
  title: string
}

interface ModalDisciplineProgressReportReturn {
  refIsOpen: ComputedRef<boolean>
  /** Without manage-progress-report the report opens read-only, as the legacy form's view mode. */
  refIsViewOnly: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refIsSaving: Ref<boolean>
  refForm: Ref<DisciplineProgressReportForm>
  refErrors: Ref<DisciplineProgressReportErrors>
  refOpenSection: Ref<DisciplineProgressReportFormGroup | null>
  refKidName: ComputedRef<string>
  refKidGrade: ComputedRef<string>
  refDisciplineName: ComputedRef<string>
  refDateRange: ComputedRef<string>
  refPositiveTraitOptions: ComputedRef<NativeSelectOption[]>
  sections: readonly DisciplineProgressReportSection[]
  setPositiveTrait: (option: NativeSelectOption) => void
  setTopics: (event: AppTextareaEvent) => void
  setStatement: (group: DisciplineProgressReportFormGroup, key: DisciplineProgressReportStatementKey, value: string) => void
  toggleSection: (group: DisciplineProgressReportFormGroup) => void
  closeModal: () => void
  submit: () => Promise<void>
}

const SECTIONS: readonly DisciplineProgressReportSection[] = [
  { group: 'progressGained', title: 'Progress gained' },
  { group: 'toImprove', title: 'Areas for improvement' },
  { group: 'furtherSteps', title: 'Further steps' },
]

/** The legacy report form named the disciplines itself rather than through the lists. */
const DISCIPLINE_NAMES: Record<string, string> = {
  MATH: 'Math',
  ELA: 'ELA',
}

const EMPTY_VALUE = '-'

export const useModalDisciplineProgressReport = (): ModalDisciplineProgressReportReturn => {
  const route = useRoute()
  const store = useDisciplineProgressReportsStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { canManageProgressReport } = useDisciplinePrivileges()

  const refForm = ref<DisciplineProgressReportForm>(createProgressReportForm(null))
  const refErrors = ref<DisciplineProgressReportErrors>(emptyProgressReportErrors())
  const refOpenSection = ref<DisciplineProgressReportFormGroup | null>(null)
  const refIsSaving = ref<boolean>(false)

  const refReport = computed<DisciplineProgressReport | null>(() => store.getEditingReport)
  const refIsOpen = computed<boolean>(() => !!refReport.value)
  const refIsViewOnly = computed<boolean>(() => !canManageProgressReport.value)
  const refTitle = computed<string>(() =>
    (refIsViewOnly.value ? 'Viewing Progress report details' : 'Editing Progress report'))

  const refKidName = computed<string>(() => disciplineStore.getDiscipline?.kid.name || EMPTY_VALUE)
  const refKidGrade = computed<string>(() => {
    const grade = disciplineStore.getDiscipline?.kid.grade
    return grade ? listsStore.getLists?.kid.grade[grade] ?? grade : EMPTY_VALUE
  })
  const refDisciplineName = computed<string>(() => {
    const code = disciplineStore.getDiscipline?.code
    return code ? DISCIPLINE_NAMES[code] ?? code : EMPTY_VALUE
  })
  /** As legacy: from when the report was written to the task's due date. */
  const refDateRange = computed<string>(() =>
    formatProgressReportDateRange(refReport.value?.created_at ?? null, refReport.value?.tutor_task?.due_date ?? null))

  /** A stored trait outside today's list is kept as an option, so opening the form does not blank it. */
  const refPositiveTraitOptions = computed<NativeSelectOption[]>(() => {
    const code = disciplineStore.getDiscipline?.code
    const traits = code ? listsStore.getLists?.positive_learning_trait?.[code] ?? [] : []
    const current = refForm.value.positiveTrait
    const values = current && !traits.includes(current) ? [current, ...traits] : traits

    return values.map(trait => ({ id: trait, value: trait, text: trait, selected: trait === current }))
  })

  const setPositiveTrait = (option: NativeSelectOption): void => {
    refForm.value.positiveTrait = String(option.value ?? '')
    refErrors.value.positiveTrait = false
  }

  const setTopics = (event: AppTextareaEvent): void => {
    refForm.value.topics = event.value
    refErrors.value.topics = false
  }

  const setStatement = (
    group: DisciplineProgressReportFormGroup,
    key: DisciplineProgressReportStatementKey,
    value: string,
  ): void => {
    refForm.value[group][key] = value
    refErrors.value[group][key] = false
  }

  const toggleSection = (group: DisciplineProgressReportFormGroup): void => {
    refOpenSection.value = refOpenSection.value === group ? null : group
  }

  const closeModal = (): void => {
    if (refIsSaving.value) return

    store.setEditingReport(null)
  }

  const submit = async (): Promise<void> => {
    const report = refReport.value
    if (!report || refIsSaving.value || refIsViewOnly.value) return

    const errors = validateProgressReportForm(refForm.value)
    refErrors.value = errors
    if (hasProgressReportErrors(errors)) return

    const disciplineId = route.params.id as string
    const isSaved = await useDisciplineProgressReportUpdateApi({
      disciplineId,
      reportId: report.id,
      payload: buildProgressReportPayload(refForm.value),
      onStart: () => {
        refIsSaving.value = true
      },
      onFinish: () => {
        refIsSaving.value = false
      },
    })

    // A refused save keeps the modal open, so the admin's edits are not lost.
    if (!isSaved) return

    store.setEditingReport(null)
    await useDisciplineProgressReportsApi({ disciplineId })
  }

  watch(refReport, (report) => {
    refForm.value = createProgressReportForm(report)
    refErrors.value = emptyProgressReportErrors()
    refOpenSection.value = null
  })

  return {
    refIsOpen,
    refIsViewOnly,
    refTitle,
    refIsSaving,
    refForm,
    refErrors,
    refOpenSection,
    refKidName,
    refKidGrade,
    refDisciplineName,
    refDateRange,
    refPositiveTraitOptions,
    sections: SECTIONS,
    setPositiveTrait,
    setTopics,
    setStatement,
    toggleSection,
    closeModal,
    submit,
  }
}
